import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Search, CheckCircle, XCircle, Clock, DollarSign, Eye, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ClaimsMedicareProPage = () => {
  const [claims, setClaims] = useState([
    { id: '1', claimNumber: 'MCR-2026-001001', patient: 'Smith, John', mrn: 'MRN-001', hicn: '1EG4-TE5-MK72', dos: '01/20/2026', amount: '$2,450.00', status: 'Paid', paidAmount: '$2,205.00', hipps: '3AFO1' },
    { id: '2', claimNumber: 'MCR-2026-001002', patient: 'Johnson, Mary', mrn: 'MRN-002', hicn: '2HK6-TF8-NL85', dos: '01/19/2026', amount: '$1,890.00', status: 'Pending', paidAmount: '-', hipps: '2BFK2' },
    { id: '3', claimNumber: 'MCR-2026-001003', patient: 'Williams, Robert', mrn: 'MRN-003', hicn: '3JL9-TG1-OP28', dos: '01/18/2026', amount: '$3,120.00', status: 'RAC Audit', paidAmount: '-', hipps: '4CGM3' },
    { id: '4', claimNumber: 'MCR-2026-001004', patient: 'Brown, Patricia', mrn: 'MRN-004', hicn: '4MN2-TH4-QR61', dos: '01/17/2026', amount: '$1,750.00', status: 'Paid', paidAmount: '$1,575.00', hipps: '2AEN1' },
    { id: '5', claimNumber: 'MCR-2026-001005', patient: 'Davis, Michael', mrn: 'MRN-005', hicn: '5PQ5-TJ7-ST94', dos: '01/16/2026', amount: '$2,680.00', status: 'ADR Requested', paidAmount: '-', hipps: '3BFO2' },
  ]);

  const [searchQuery, setSearchQuery] = useState('');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Paid</span>;
      case 'Pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case 'RAC Audit':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> RAC Audit</span>;
      case 'ADR Requested':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> ADR Requested</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const filteredClaims = claims.filter(claim =>
    claim.patient.toLowerCase().includes(searchQuery.toLowerCase()) ||
    claim.claimNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    claim.hicn.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="claims-medicare-pro-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-800 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Claims Management Medicare Pro</h1>
            <p className="text-gray-600">Inovalon Claim Portal - Medicare Home Health Claims</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-gray-900">{claims.length}</p>
            <p className="text-sm text-gray-500">Total Claims</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-green-600">{claims.filter(c => c.status === 'Paid').length}</p>
            <p className="text-sm text-gray-500">Paid</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-yellow-600">{claims.filter(c => c.status === 'Pending').length}</p>
            <p className="text-sm text-gray-500">Pending</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-red-600">{claims.filter(c => c.status === 'RAC Audit').length}</p>
            <p className="text-sm text-gray-500">RAC Audits</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-2xl font-bold text-orange-600">{claims.filter(c => c.status === 'ADR Requested').length}</p>
            <p className="text-sm text-gray-500">ADRs</p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by patient, claim #, or HICN..." className="pl-10" />
          </div>
        </div>

        {/* Claims Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Claim #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">HICN/MBI</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">HIPPS</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">DOS</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Billed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClaims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-red-600 font-medium">{claim.claimNumber}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{claim.patient}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{claim.hicn}</td>
                    <td className="px-4 py-3 font-mono text-purple-600 font-semibold">{claim.hipps}</td>
                    <td className="px-4 py-3 text-gray-600">{claim.dos}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{claim.amount}</td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(claim.status)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">{claim.paidAmount}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => toast.info(`Viewing claim ${claim.claimNumber}`)}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClaimsMedicareProPage;
